import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Eye, Trash2, Users, Image as ImageIcon, Pencil, Clock, CheckCircle2, Flag } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import ListingForm, { EditableListing } from '@/components/ListingForm';
import AddonsPanel from '@/components/AddonsPanel';
import HomeownerResponses from '@/components/HomeownerResponses';
import JobAlertsPanel from '@/components/JobAlertsPanel';
import { LeadBalanceSection, MyLeadsSection } from '@/components/ContractorLeadTools';
import { isUnderReview } from '@/lib/marketplace';
import { CLOSABLE_STATUSES, ClosingOutcome, outcomeDef } from '@/lib/projectClosing';
import ProjectClosingDialog, { ClosingTarget } from '@/components/ProjectClosingDialog';
import MyResultsCard from '@/components/contractor/MyResultsCard';

import { Listing } from '@/components/JobCard';
import { tradeLabel, localizedSlug } from '@/data/constants';
import { coverImageFor, normalizePhotos } from '@/lib/coverImage';
import CoverImage from '@/components/CoverImage';




const OwnerDashboard: React.FC<{ mode: 'project' | 'workorder' }> = ({ mode }) => {
  const { t, path, lang } = useI18n();
  const { profile } = useAuth();
  const [items, setItems] = useState<Listing[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<EditableListing | null>(null);


  const load = async () => {
    if (!profile) return;
    const { data } = await supabase
      .from('mkt_listings')
      .select('*')
      .eq('owner_id', profile.id)
      .order('created_at', { ascending: false });
    const rows = (data || []) as Listing[];
    setItems(rows);
    if (rows.length) {
      const { data: purch } = await supabase
        .from('mkt_lead_purchases')
        .select('listing_id')
        .in('listing_id', rows.map((r) => r.id));
      const c: Record<string, number> = {};
      (purch || []).forEach((p: { listing_id: string }) => (c[p.listing_id] = (c[p.listing_id] || 0) + 1));
      setCounts(c);
    }
  };

  useEffect(() => {
    load();
  }, [profile?.id]);

  const remove = async (id: string) => {
    await supabase.from('mkt_listings').delete().eq('id', id);
    setItems((p) => p.filter((x) => x.id !== id));
  };

  // Structured project-closing flow (see src/lib/projectClosing.ts).
  const [closing, setClosing] = useState<{ listing: ClosingTarget; initial?: ClosingOutcome } | null>(null);
  const [completeMsg, setCompleteMsg] = useState('');
  const [closedTick, setClosedTick] = useState(0);
  const handleClosed = (id: string, outcome: ClosingOutcome, status: string, rated?: boolean) => {
    const es = lang === 'es';
    setClosing(null);
    setItems((p) => p.map((x) => (x.id === id ? ({ ...x, status: status || x.status, closing_outcome: outcome } as Listing) : x)));
    setCompleteMsg(
      outcome === 'work_completed'
        ? rated
          ? es ? '¡Gracias por calificar al contratista! Pronto te pediremos una breve reseña.' : 'Thanks for rating your contractor! We will ask you for a short review soon.'
          : es ? '¡Trabajo marcado como terminado! Pronto te pediremos una breve reseña.' : 'Work marked as completed! We will ask you for a short review soon.'
        : outcome === 'hired_contractor'
        ? es ? '¡Felicidades por contratar! Márcalo como terminado cuando el trabajo esté listo.' : 'Congrats on hiring! Mark it completed once the work is done.'
        : es ? 'Proyecto cerrado. Ya no se mostrará a nuevos contratistas.' : 'Project closed. It will no longer be shown to new contractors.'
    );
    setClosedTick((n) => n + 1);
    load();
  };

  return (
    <div>
      {/* GC-only: remaining lead credits + renewal countdown. A GC who buys a
          membership or a single lead needs to see the balance they paid for. */}
      {mode === 'workorder' && (
        <section className="mb-8">
          <LeadBalanceSection />
        </section>
      )}

      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-6 sm:p-8">
        <h2 className="text-xl font-extrabold text-white sm:text-2xl">
          {mode === 'workorder' ? t('home.gcTitle') : t('home.homeownerTitle')}
        </h2>
        <p className="mt-1 max-w-lg text-sm text-slate-300">
          {mode === 'workorder' ? t('home.gcDesc') : t('home.homeownerDesc')}
        </p>
        <button
          onClick={() => setOpen(true)}
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-3.5 text-base font-bold text-white hover:bg-orange-600"
        >
          <Plus className="h-5 w-5" /> {mode === 'workorder' ? t('dash.postWorkOrder') : t('dash.postProject')}
        </button>
      </div>

      <section className="mt-10">
        <h3 className="text-xl font-extrabold text-slate-900">
          {mode === 'workorder' ? t('dash.myWorkOrders') : t('dash.myProjects')}
        </h3>
        {completeMsg ? (
          <p role="status" className="mt-3 flex items-center gap-2 rounded-xl bg-sky-50 px-4 py-2.5 text-sm font-semibold text-sky-800">
            <CheckCircle2 className="h-4 w-4 shrink-0" /> {completeMsg}
          </p>
        ) : null}
        {items.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
            {mode === 'workorder' ? t('dash.noWorkOrders') : t('dash.noProjects')}
          </p>
        ) : (
          <div className="mt-5 space-y-4">
            {items.map((l) => (
              <div key={l.id} className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center">
                {(() => {
                  // Same resolver + same runtime degradation as the marketplace
                  // cards. A raw <img> here had no onError, so a deleted upload
                  // painted a broken/blank box on the owner's own dashboard.
                  const photos = normalizePhotos(l.photos);
                  const { isRealPhoto } = coverImageFor(l);
                  return (
                    <div className="relative w-full shrink-0 sm:w-28">
                      <CoverImage
                        listing={l}
                        alt={isRealPhoto ? l.title : ''}
                        aspect="h-20"
                        className="rounded-xl"
                      >
                        {photos.length > 1 && (
                          <span className="absolute bottom-1 right-1 flex items-center gap-1 rounded-full bg-slate-900/75 px-1.5 py-0.5 text-[10px] font-bold text-white">
                            <ImageIcon className="h-3 w-3" />
                            {photos.length}
                          </span>
                        )}
                      </CoverImage>
                    </div>
                  );
                })()}

                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-slate-900 px-2.5 py-1 text-xs font-bold uppercase text-white">
                      {tradeLabel(l.trade, lang)}
                    </span>
                    {/* Risk-gate statuses (pending_review / held / blocked) are shown to the poster as ONE neutral,
                        bilingual "Under review" badge. The specific status, score and triggered rules are never exposed. */}
                    {isUnderReview(l.status) ? (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold uppercase text-amber-800 ring-1 ring-amber-200"
                        title={lang === 'es'
                          ? 'Revisamos algunos proyectos antes de publicarlos. Te avisaremos por correo cuando termine la revisión.'
                          : 'Some projects are reviewed before they go live. We will email you when the review is complete.'}
                      >
                        <Clock className="h-3.5 w-3.5" /> {lang === 'es' ? 'En revisión' : 'Under review'}
                      </span>
                    ) : outcomeDef((l as any).closing_outcome) && ['hired', 'completed', 'cancelled'].includes(String(l.status)) ? (
                      (() => {
                        const d = outcomeDef((l as any).closing_outcome)!;
                        return (
                          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold uppercase ring-1 ${d.tone}`}>
                            <CheckCircle2 className="h-3.5 w-3.5" /> {lang === 'es' ? d.es : d.en}
                          </span>
                        );
                      })()
                    ) : l.status === 'completed' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold uppercase text-sky-800 ring-1 ring-sky-200">
                        <CheckCircle2 className="h-3.5 w-3.5" /> {lang === 'es' ? 'Completado' : 'Completed'}
                      </span>
                    ) : (
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase ${
                          l.status === 'removed' || l.status === 'rejected' ? 'bg-red-50 text-red-700' : l.status === 'sold_out' ? 'bg-slate-100 text-slate-700' : 'bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        {l.status === 'sold_out'
                          ? lang === 'es' ? 'Reclamado por 3 contratistas' : 'Claimed by 3 contractors'
                          : l.status === 'rejected'
                          ? lang === 'es' ? 'No publicado' : 'Not published'
                          : t(`status.${l.status || 'active'}`)}
                      </span>
                    )}
                  </div>
                  {isUnderReview(l.status) && (
                    <p className="mt-2 text-xs text-amber-800">
                      {lang === 'es'
                        ? 'Tu proyecto está guardado y en revisión. Aún no es visible para los contratistas; te enviaremos un correo cuando se publique.'
                        : 'Your project is saved and under review. It is not visible to contractors yet — we will email you as soon as it is published.'}
                    </p>
                  )}

                  <h4 className="mt-2 font-bold text-slate-900">{l.title}</h4>
                  <p className="text-sm text-slate-500">
                    {l.city}, {l.state} · {l.budget_range || '—'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 rounded-lg bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700">
                    <Users className="h-4 w-4 text-slate-400" />
                    {counts[l.id] || 0}
                    <span className="text-xs font-semibold text-slate-400">
                      {mode === 'workorder' ? t('dash.applicants') : t('dash.projectResponses')}
                    </span>
                  </span>
                  <Link
                    to={path(`/jobs/${localizedSlug(l.slug, l.trade, lang)}`)}
                    className="rounded-lg border border-slate-300 p-2.5 text-slate-600 hover:bg-slate-50"
                    aria-label={t('jobs.viewDetails')}
                  >
                    <Eye className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={() => setEditing(l)}
                    className="rounded-lg border border-slate-300 p-2.5 text-slate-600 hover:bg-slate-50"
                    aria-label={t('dash.editProject')}
                    title={t('dash.editProject')}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>

                  {/* Homeowner-only structured closing flow. The DB trigger maps the outcome to a status;
                      "Work completed" stamps completed_at, which the daily testimonial job uses to send the
                      existing one-time review request. */}
                  {mode === 'project' && l.status === 'hired' && (
                    <button
                      onClick={() => setClosing({ listing: l as ClosingTarget, initial: 'work_completed' })}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-sky-300 px-3 py-2 text-xs font-bold text-sky-700 hover:bg-sky-50"
                      title={lang === 'es' ? 'Marcar trabajo terminado' : 'Mark work completed'}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      <span className="hidden sm:inline">{lang === 'es' ? 'Trabajo terminado' : 'Work completed'}</span>
                    </button>
                  )}
                  {mode === 'project' && CLOSABLE_STATUSES.includes(String(l.status || 'published')) && l.status !== 'hired' && (
                    <button
                      onClick={() => setClosing({ listing: l as ClosingTarget })}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 px-3 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-50"
                      title={lang === 'es' ? 'Cerrar proyecto' : 'Close project'}
                    >
                      <Flag className="h-4 w-4" />
                      <span className="hidden sm:inline">{lang === 'es' ? 'Cerrar proyecto' : 'Close project'}</span>
                    </button>
                  )}

                  <button onClick={() => remove(l.id)} className="rounded-lg border border-slate-300 p-2.5 text-red-600 hover:bg-red-50" aria-label={t('common.delete')}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Homeowner-only: who unlocked my projects (never shows lead price or balances) */}
      {mode === 'project' && <HomeownerResponses refreshKey={closedTick} />}

      {/* GC-only: general contractors also BUY leads in the trades they
          self-perform, so they get the same saved-search / job-alert panel as
          subcontractors. Homeowners post work rather than look for it, so the
          panel is deliberately not shown on the homeowner dashboard. */}
      {mode === 'workorder' && <JobAlertsPanel />}

      {/* GC-only: general contractors BUY leads as well as post trade packages
          (canBuyLeads() is true for 'gc', the purchase RLS policies allow it and
          the pricing page sells them memberships). Without these panels a GC
          could pay for credits or a single lead and have nowhere to see the
          balance, open the lead they bought or check their plan. Same component
          the subcontractor dashboard renders — one implementation, two roles. */}
      {mode === 'workorder' && <MyLeadsSection />}
      {mode === 'workorder' && (
        <div className="mt-8">
          <MyResultsCard />
        </div>
      )}

      {mode === 'workorder' && <AddonsPanel />}



      {mode === 'project' && (
        <ProjectClosingDialog
          listing={closing?.listing || null}
          initialOutcome={closing?.initial}
          onClose={() => setClosing(null)}
          onClosed={handleClosed}
        />
      )}

      <ListingForm
        mode={mode}
        open={open || !!editing}
        listing={editing}
        onClose={() => {
          setOpen(false);
          setEditing(null);
          load();
        }}
        onCreated={load}
      />
    </div>
  );
};

export default OwnerDashboard;

